import { useQuery } from "@tanstack/react-query";
import { ArrowRight, MessageSquare } from "lucide-react";
import { Link } from "react-router";

import { getConversations } from "@/api/chat/chat";
import { RECENT_CONVERSATIONS_KEY } from "@/api/chat/conversationListKeys";
import { signalOnceCached } from "@/api/querySignal";
import type { IChatConversationListItem } from "@/api/chat/chat.types";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardAction,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "@/components/ui/card";
import {
    Item,
    ItemContent,
    ItemDescription,
    ItemGroup,
    ItemMedia,
    ItemTitle,
} from "@/components/ui/item";
import { activityTime, getConversationTitle } from "@/utils/chatConversations";

import { RECENT_LIMIT, staggerStyle } from "./recentWidget";
import {
    RecentWidgetEmpty,
    RecentWidgetError,
    RecentWidgetSkeleton,
    RelativeTime,
} from "./recentWidgetParts";

/** Show the viewer's 5 most recently active Polly chats from their own query, in server order. */
export function RecentConversations() {
    const { data, isPending, isError, refetch } = useQuery({
        queryKey: RECENT_CONVERSATIONS_KEY,
        queryFn: (context) =>
            getConversations(
                1,
                RECENT_LIMIT,
                { sort_by: "recently_active" },
                undefined,
                undefined,
                { signal: signalOnceCached(context) }
            ),
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

    const conversations = (data?.items ?? []).slice(0, RECENT_LIMIT);

    return (
        <Card className="h-full">
            <CardHeader>
                <CardTitle>Recent conversations</CardTitle>
                <CardDescription>Pick up a thread with Polly</CardDescription>
                <CardAction>
                    <Button
                        variant="link"
                        size="sm"
                        className="text-muted-foreground hover:text-primary h-auto gap-1 px-1"
                        render={<Link to="/organizations/chat" />}
                    >
                        Ask Polly
                        <ArrowRight className="size-3.5 transition-transform duration-150 group-hover/button:translate-x-0.5 motion-reduce:transition-none" />
                    </Button>
                </CardAction>
            </CardHeader>
            <CardContent>
                {isPending ? (
                    <RecentWidgetSkeleton noun="conversations" />
                ) : isError ? (
                    <RecentWidgetError
                        noun="conversations"
                        onRetry={() => refetch()}
                    />
                ) : conversations.length === 0 ? (
                    <RecentWidgetEmpty
                        icon={<MessageSquare />}
                        title="No conversations yet"
                        description="Start a conversation with Polly above and it will show up here."
                    />
                ) : (
                    <ItemGroup className="gap-1">
                        {conversations.map((conversation, index) => (
                            <ConversationRow
                                key={conversation.id}
                                conversation={conversation}
                                index={index}
                            />
                        ))}
                    </ItemGroup>
                )}
            </CardContent>
        </Card>
    );
}

function ConversationRow({
    conversation,
    index,
}: {
    conversation: IChatConversationListItem;
    index: number;
}) {
    const title = getConversationTitle(conversation);
    // Agent + client tag, when present — the producer's mental anchors for a
    // thread. Either may be absent; an untagged thread shows just its title.
    const meta = [conversation.agent_name, conversation.client?.name]
        .filter(Boolean)
        .join(" · ");

    return (
        <Item
            render={<Link to={`/organizations/chat/${conversation.id}`} />}
            className="group/row animate-in fade-in-0 slide-in-from-bottom-1 motion-reduce:animate-none"
            style={staggerStyle(index)}
        >
            <ItemMedia
                variant="icon"
                className="bg-muted text-muted-foreground group-hover/row:bg-primary/10 group-hover/row:text-primary size-8 self-center rounded-lg transition-colors duration-150 motion-reduce:transition-none"
            >
                <MessageSquare />
            </ItemMedia>
            <ItemContent className="min-w-0 gap-0.5">
                <ItemTitle className="block w-full truncate" title={title}>
                    {title}
                </ItemTitle>
                {meta ? (
                    <ItemDescription className="line-clamp-1">
                        {meta}
                    </ItemDescription>
                ) : null}
            </ItemContent>
            <RelativeTime iso={activityTime(conversation)} />
        </Item>
    );
}
